import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import LineChart from "../ui/LineChart";

const EffectiveChart = () => {
  const [chains, setChains] = useState([]);
  const [selectedChain, setSelectedChain] = useState("");
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [model, setModel] = useState(null);
  const [lineChartDataEffective, setLineChartDataEffective] = useState({
    labels: [],
    datasets: [{ data: [] }, { data: [] }],
  });
  const [error, setError] = useState(null);

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        title: {
          display: false,
          text: "Date",
        },
      },
      y: {
        title: {
          display: true,
          text: "Workers",
        },
      },
    },
  };

  const handleSelectChange = (e) => {
    setSelectedChain(e.target.value);
    setModels([]);
    setSelectedModel("");
    setModel(null);
    setLineChartDataEffective({
      labels: [],
      datasets: [{ data: [] }, { data: [] }],
    });
  };

  const handleModelChange = (e) => {
    const modelId = e.target.value;
    const selected = models.find((item) => String(item.id) === modelId) || null;
    setSelectedModel(modelId);
    setModel(selected);
    setLineChartDataEffective({
      labels: [],
      datasets: [{ data: [] }, { data: [] }],
    });
  };

  useEffect(() => {
    const fetchChains = async () => {
      try {
        const response = await api.get("/chains");
        setChains(response.data);
      } catch (error) {
        console.error("Error fetching chains:", error);
      }
    };

    fetchChains();
  }, []);

  useEffect(() => {
    const fetchModelsByChain = async () => {
      if (selectedChain) {
        try {
          const response = await api.get(`/models-by-chain/${selectedChain}`);
          const chainModels = response.data || [];
          const latestModel = chainModels[0] || null;
          setModels(chainModels);
          setSelectedModel(latestModel ? String(latestModel.id) : "");
          setModel(latestModel);
        } catch (error) {
          console.error("Error fetching models by chain:", error);
          setModels([]);
          setSelectedModel("");
          setModel(null);
        }
      }
    };

    fetchModelsByChain();
  }, [selectedChain]);

  useEffect(() => {
    if (model && selectedChain) fetchEffectiveData(model.id);
  }, [model, selectedChain]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const fetchEffectiveData = async (modelId) => {
    setError(null);
    try {
      // Fetch standard effective data for the selected chain
      const effectiveResponse = await api.get(
        `/effective_data_total/${modelId}/${selectedChain}`
      );
      const effectiveData = effectiveResponse.data;

      const standardDirectData = Object.values(effectiveData).map(
        (data) => data.effectifDirects
      );
      const standardIndirectData = Object.values(effectiveData).map(
        (data) => data.effectifIndirects
      );

      // Fetch real effective data for the selected chain
      const effectiveRealResponse = await api.get(
        `/effective_real_total/${modelId}/${selectedChain}`
      );
      const effectiveRealData = effectiveRealResponse.data;
      const dates = Object.keys(effectiveRealData).map(formatDate);

      const realDirectData = Object.values(effectiveRealData).map(
        (data) => data.effectifDirects
      );
      const realIndirectData = Object.values(effectiveRealData).map(
        (data) => data.effectifIndirects
      );

      // Set line chart data
      setLineChartDataEffective({
        labels: dates,
        datasets: [
          {
            label: "Standard Direct",
            data: standardDirectData,
            borderColor: "rgba(75,192,192,1)",
            backgroundColor: "rgba(75,192,192,0.2)",
          },
          {
            label: "Standard Indirect",
            data: standardIndirectData,
            borderColor: "rgba(75,75,192,1)",
            backgroundColor: "rgba(75,75,192,0.2)",
          },
          {
            label: "Real Direct",
            data: realDirectData,
            borderColor: "rgba(153,102,255,1)",
            backgroundColor: "rgba(153,102,255,0.2)",
          },
          {
            label: "Real Indirect",
            data: realIndirectData,
            borderColor: "rgba(153,102,75,1)",
            backgroundColor: "rgba(153,102,75,0.2)",
          },
        ],
      });
    } catch (error) {
      console.log(error);
      setError(error.response?.data?.message || "An error occurred");
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Suivi d'Effectif en graphique        </h2>
          <p className="text-gray-600 mt-1">Suivi de l'effectif standard vs réel dans le temps</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner une Chaîne
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white shadow-sm"
              value={selectedChain}
              onChange={handleSelectChange}
            >
              <option value="">Choisir une chaîne...</option>
              {chains &&
                chains.map((chain) => (
                  <option key={chain.id} value={chain.id}>
                    {chain.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="w-full sm:w-56">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un Modèle
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white shadow-sm"
              value={selectedModel}
              onChange={handleModelChange}
              disabled={!selectedChain || models.length === 0}
            >
              <option value="">Choisir un modèle...</option>
              {models.map((item, index) => (
                <option key={item.id} value={item.id}>
                  {item.modele}{index === 0 ? " — Plus récent" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {model && (
        <div className="mt-4">
          <p><strong>Modèle:</strong> {model.modele}</p>
          <p><strong>Catégorie:</strong> {model.category}</p>
        </div>
      )}

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <LineChart 
            data={lineChartDataEffective} 
            options={lineChartOptions}
            title="Chronologie de l'Effectif"
            subtitle="Comparaison de l'effectif standard vs réel"
          />
        </div>
      )}
    </div>
  );
};

export default EffectiveChart;
